import { AvailabilityFilterItem } from "PFApp/components/filters/filter_items";
import { useBufferedFilter } from "PFApp/components/filters_v2/hooks/use_buffered_filter";
import { Button } from "PFComponents/button";
import SidePanel from "PFComponents/side_panel/side_panel";
import { Typography } from "PFComponents/typography";
import { decamelizeDesiredKeys } from "PFCore/helpers/keys_camelization_utils";
import { AvailabilityRuleV2, FilterWithDefaults } from "PFTypes";
import { KnownFiltersV2 } from "PFTypes/filters_v2";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "./availability_side_panel.module.scss";

type AvailabilitySidePanelProps = {
  show: boolean;
  disabled: boolean;
  onClose: VoidFunction;
};

export const AvailabilitySidePanel = ({ show, disabled, onClose }: AvailabilitySidePanelProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.sidePanel.filters" });
  const {
    canClear,
    clear,
    canRestore,
    restore,
    filter: availabilityFilter,
    update
  } = useBufferedFilter(KnownFiltersV2.Availability);

  const legacyAvailabilityFilter = useMemo(
    () => decamelizeDesiredKeys(availabilityFilter, ["value"]) as FilterWithDefaults,
    [availabilityFilter]
  );

  const handleValueUpdate = useCallback(
    (updatedValue: Partial<AvailabilityRuleV2> | null) => {
      update({
        value: {
          ...((availabilityFilter.value as AvailabilityRuleV2) || {}),
          ...updatedValue
        }
      });
    },
    [availabilityFilter.value, update]
  );

  return (
    <SidePanel
      show={show}
      title={
        <div className={css.title}>
          <Typography variant="h4">{t("availability")}</Typography>
          {canClear && <Button text={t("clear")} icon="filter-clean" kind="text" onClick={clear} />}
          {canRestore && <Button text={t("restore")} icon="history" kind="text" onClick={restore} />}
        </div>
      }
      onClose={onClose}
      size="medium"
      fullHeight
    >
      <div className={css.contentWrapper}>
        {disabled && <div className={css.loadingOverlay} />}
        <AvailabilityFilterItem
          allowClearRestore={false}
          filter={legacyAvailabilityFilter}
          handleChange={handleValueUpdate}
        />
      </div>
    </SidePanel>
  );
};
